import { type ApolloClient } from '@apollo/client';

import { PricingSetFragment, RateGroupKindEnum } from '@graphql/platform';

import { CheckoutType } from '@root/components/checkout/types';
import { PlanName } from '@root/resources/types/plan_name';

import { SafeLocalStorage, SafeSessionStorage } from '@utils/browser';
import { DefaultSerializable, Serializer } from '@utils/serializable';

import { StorageCheckoutData } from '../data';
import { parseCheckoutQueryString } from './checkout_url';
import { updateStorageDraft } from './drafts';

/** Challenger funnel defaults: 8-month minimum + 5′×10′. */
const CHALLENGER_DEFAULT_COMMITMENT = RateGroupKindEnum.Saver;
export const CHALLENGER_DEFAULT_PLAN_SIZE: PlanName = '5x10';

export const withChallengerStorageDefaults = (
  data: StorageCheckoutData,
): StorageCheckoutData => ({
  ...data,
  commitment: data.commitment ?? CHALLENGER_DEFAULT_COMMITMENT,
  planSize: data.planSize ?? CHALLENGER_DEFAULT_PLAN_SIZE,
});

const STORAGE_KEY = '__clutter:checkout_data:storage';
const MOVING_KEY = '__clutter:checkout_data:moving';
const BOOKED_KEY = '__clutter:checkout_data:booked';

const MAX_DURATION = 1000 * 60 * 60; // one hour in milliseconds
const BOOKED_FLAG_DURATION = 1000 * 60 * 15; // 15 minutes in milliseconds

type PersistedData<T> = {
  data: T;
  /** Created/updated timestamp in epoch milliseconds */
  ts: number;
};

type StorageSessionPersistedCheckoutData = PersistedData<StorageCheckoutData>;

const serializer = new Serializer();

export const setBookedFlag = () =>
  SafeLocalStorage.setItem(BOOKED_KEY, new Date().toISOString());

export const fetchBookedFlag = () => {
  const value = SafeLocalStorage.getItem(BOOKED_KEY);
  if (value) return Date.parse(value) > Date.now() - BOOKED_FLAG_DURATION;
  return false;
};

export const clearBookedFlag = () => SafeLocalStorage.removeItem(BOOKED_KEY);

export const clearSessionCheckoutData = () => {
  SafeSessionStorage.removeItem(STORAGE_KEY);
  SafeSessionStorage.removeItem(MOVING_KEY);
};

export const persistStorageSessionCheckoutData = (
  client: ApolloClient,
  data: StorageCheckoutData,
  leadToken?: string,
  pricingSet?: PricingSetFragment,
) => {
  const serialized = serializer.serialize({
    data,
    ts: Date.now(),
  });
  updateStorageDraft(client, data, leadToken, pricingSet);
  SafeSessionStorage.setItem(STORAGE_KEY, serialized);
};

const getSessionStorageData = <D extends DefaultSerializable>(key: string) => {
  const { data, ts = 0 } = serializer.deserialize<PersistedData<D>>(
    SafeSessionStorage.getItem(key) || '{}',
  );

  if (ts + MAX_DURATION > Date.now()) return data;
};

type RestoreStorageSessionOptions = {
  applyChallengerDefaults?: boolean;
};

export const restoreStorageSessionCheckoutData = (
  options?: RestoreStorageSessionOptions,
): Omit<StorageSessionPersistedCheckoutData, 'ts'> => {
  const applyChallengerDefaults = options?.applyChallengerDefaults ?? false;
  const data = getSessionStorageData<StorageCheckoutData>(STORAGE_KEY);

  if (data) {
    if (applyChallengerDefaults) {
      return { data: withChallengerStorageDefaults(data) };
    }
    return { data: { ...data } };
  }

  const { zip, zipValidated } = parseCheckoutQueryString(
    window.location.search,
  );

  const emptyData: StorageCheckoutData = {
    checkoutType: CheckoutType.Standard,
    zip,
    zipValidated,
  };

  if (applyChallengerDefaults) {
    return {
      data: withChallengerStorageDefaults(emptyData),
    };
  }

  return { data: emptyData };
};
